/**
 * Reference-counted background scroll lock for document.body, html, and optional scroll roots.
 * Multiple overlapping callers (e.g. stacked modals) share one snapshot; the page restores only when the last caller releases.
 */

const BACKGROUND_SCROLL_LOCK_ROOT_IDS = ['list-scroll', 'minutes-actions-scroll'] as const

/** Real iOS / iPadOS Safari often ignores overflow:hidden on body; fixed + scroll offset is the usual fix. */
export const isAppleTouchDocument = (): boolean => {
    if (typeof navigator === 'undefined') {
        return false
    }
    const { userAgent, platform, maxTouchPoints } = navigator
    if (/iPhone|iPod/.test(userAgent) || /iPad/.test(userAgent)) {
        return true
    }
    return platform === 'MacIntel' && maxTouchPoints > 1
}

type LockedScrollElement = {
    el: HTMLElement
    overflow: string
    touchAction: string
    overscrollBehavior: string
}

type IosViewportScrollLockSnapshot = {
    scrollY: number
    bodyPosition: string
    bodyTop: string
    bodyLeft: string
    bodyRight: string
    bodyWidth: string
}

type BackgroundScrollLockSnapshot = {
    bodyOverflow: string
    htmlOverflow: string
    bodyOverscrollBehavior: string
    htmlOverscrollBehavior: string
    lockedElements: LockedScrollElement[]
    iosViewportLock: IosViewportScrollLockSnapshot | null
}

let refCount = 0
let snapshot: BackgroundScrollLockSnapshot | null = null

const applyLockStyles = (lock: BackgroundScrollLockSnapshot) => {
    for (const id of BACKGROUND_SCROLL_LOCK_ROOT_IDS) {
        const el = document.getElementById(id)
        if (el) {
            el.style.overflow = 'hidden'
            el.style.touchAction = 'none'
            el.style.overscrollBehavior = 'none'
        }
    }
    if (lock.iosViewportLock) {
        const y = lock.iosViewportLock.scrollY
        document.body.style.position = 'fixed'
        document.body.style.top = `-${y}px`
        document.body.style.left = '0'
        document.body.style.right = '0'
        document.body.style.width = '100%'
    }
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overscrollBehavior = 'none'
    document.documentElement.style.overscrollBehavior = 'none'
}

const takeSnapshot = (): BackgroundScrollLockSnapshot => {
    const lockedElements: LockedScrollElement[] = []
    for (const id of BACKGROUND_SCROLL_LOCK_ROOT_IDS) {
        const el = document.getElementById(id)
        if (el) {
            lockedElements.push({
                el,
                overflow: el.style.overflow,
                touchAction: el.style.touchAction,
                overscrollBehavior: el.style.overscrollBehavior
            })
        }
    }
    let iosViewportLock: IosViewportScrollLockSnapshot | null = null
    if (isAppleTouchDocument()) {
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0
        iosViewportLock = {
            scrollY,
            bodyPosition: document.body.style.position,
            bodyTop: document.body.style.top,
            bodyLeft: document.body.style.left,
            bodyRight: document.body.style.right,
            bodyWidth: document.body.style.width
        }
    }
    return {
        bodyOverflow: document.body.style.overflow,
        htmlOverflow: document.documentElement.style.overflow,
        bodyOverscrollBehavior: document.body.style.overscrollBehavior,
        htmlOverscrollBehavior: document.documentElement.style.overscrollBehavior,
        lockedElements,
        iosViewportLock
    }
}

const restoreSnapshot = (lock: BackgroundScrollLockSnapshot) => {
    document.body.style.overflow = lock.bodyOverflow
    document.documentElement.style.overflow = lock.htmlOverflow
    document.body.style.overscrollBehavior = lock.bodyOverscrollBehavior
    document.documentElement.style.overscrollBehavior = lock.htmlOverscrollBehavior
    for (const { el, overflow, touchAction, overscrollBehavior } of lock.lockedElements) {
        if (el.isConnected) {
            el.style.overflow = overflow
            el.style.touchAction = touchAction
            el.style.overscrollBehavior = overscrollBehavior
        }
    }
    if (lock.iosViewportLock) {
        const v = lock.iosViewportLock
        document.body.style.position = v.bodyPosition
        document.body.style.top = v.bodyTop
        document.body.style.left = v.bodyLeft
        document.body.style.right = v.bodyRight
        document.body.style.width = v.bodyWidth
        window.scrollTo(0, v.scrollY)
    }
}

/**
 * Increment lock refcount. On first acquire, snapshot inline styles and apply scroll lock.
 */
export const acquireBackgroundScrollLock = (): void => {
    if (typeof document === 'undefined') {
        return
    }
    refCount++
    if (refCount === 1) {
        snapshot = takeSnapshot()
        applyLockStyles(snapshot)
    }
}

/**
 * Re-apply lock styles without changing refcount (e.g. after layout / rAF on iOS). No-op if nothing holds the lock.
 */
export const reapplyBackgroundScrollLock = (): void => {
    if (typeof document === 'undefined' || refCount === 0 || !snapshot) {
        return
    }
    applyLockStyles(snapshot)
}

/**
 * Decrement refcount. When it reaches zero, restore snapshotted styles. Safe to call multiple times after a full release (no-op).
 */
export const releaseBackgroundScrollLock = (): void => {
    if (typeof document === 'undefined' || refCount === 0) {
        return
    }
    refCount--
    if (refCount === 0 && snapshot) {
        const lock = snapshot
        snapshot = null
        restoreSnapshot(lock)
    }
}
